import { getCinemas } from '../../utils/GetCinemas';
import {
  getFilmsAndShowtimes,
  getFilmsAndShowtimesResult,
  Showtime,
} from '../../utils/GetFilmsAndShowtimes';
import { getFromDate, getNextWeekDate } from '../../utils/dates';
import { getBestPoster, getBestTitle } from '../../utils/GetLocalizedMovies';
import { getShowtimeTags } from '../../utils/tags';
import { milesToKm } from '../../utils/AthenaApi';

const apiLimit = 120;

const toDate = getNextWeekDate();
const fromDate = getFromDate();

export type FilmType = {
  movie: {
    localePoster: {
      url: string;
    };
    localeTitle: string;
    localeCertificate: string;
  };
  showtimesFormatted: Array<NewShowtimeType>;
};

export type NewShowtimeType = {
  date: string;
  dateFormatted: string;
  times: Array<{
    time: string;
    url: string;
    tags: string[];
  }>;
};

export const searchCinemas = async (request: any, locationRadius: string) => {
  let searchTerm: string | null = null;
  let location: { lat: number; lon: number } | null = null;
  let maxDistanceKm: number | null = null;

  if (request && request.search) {
    searchTerm = request.search;
  }

  if (request && request.location) {
    location = { lat: request.location.lat, lon: request.location.lon };
    maxDistanceKm = milesToKm(parseInt(locationRadius, 10));
  }

  // retrieve all cinemas matching the search
  const cinemas = await getCinemas({
    apiLimit,
    location,
    maxDistanceKm,
    searchTerm,
  });

  // Athena already scopes to relevant theaters, so the old has-showtimes prefilter
  // is gone. For a text search, still narrow to an exact city match (the original
  // behaviour) since Athena `search` also matches on name.
  return cinemas
    .filter(({ node }) => {
      if (typeof request.search !== 'undefined') {
        return (
          node.location.city.toLowerCase() === request.search.toLowerCase()
        );
      }
      return true;
    })
    .map(({ node }) => {
      return node;
    })
    // Athena has no ordering arg (old API: [ALPHABETICAL] / [CLOSEST, ALPHABETICAL]).
    .sort((a, b) => {
      if (location) {
        const da = parseFloat(a.coordinates.distance) || 0;
        const db = parseFloat(b.coordinates.distance) || 0;
        if (da !== db) {
          return da - db;
        }
      }
      return a.name.localeCompare(b.name, 'en');
    });
};

export const searchCinemaShowtimes = async (
  cinemaId: string,
  screeningType: string
) => {
  const filmsObj = await getFilmsAndShowtimes({
    cinemaId,
    fromDate,
    toDate,
    showtimeTags: screeningType ? getShowtimeTags(screeningType) : [],
  });

  return filmsObj
    .map(
      (result: getFilmsAndShowtimesResult): FilmType => {
        const { movie, showtimes } = result;
        return {
          movie: {
            localePoster: {
              url: getBestPoster(movie),
            },
            localeTitle: getBestTitle(movie),
            localeCertificate:
              movie.mainRelease && movie.mainRelease.certificate
                ? movie.mainRelease.certificate
                : 'tbc',
          },
          showtimesFormatted: formatShowtimes(showtimes),
        };
      }
    )
    .sort((a, b) =>
      a.movie.localeTitle.localeCompare(b.movie.localeTitle, 'en')
    );
};

export const formatShowtimes = (showtimes: Array<Showtime>) => {
  const showtimesData: Array<NewShowtimeType> = [];

  let dateCounter = -1;

  showtimes.forEach((showtime: Showtime) => {
    const tempDate = showtime.startsAt.split(' ')[0];
    const tempDateSplit = tempDate.split('-');
    const d = new Date(
      parseInt(tempDateSplit[0], 10),
      parseInt(tempDateSplit[1], 10) - 1,
      parseInt(tempDateSplit[2], 10)
    );
    const tempDateFormatted = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
      year: 'numeric',
    });

    const tempTime = showtime.startsAt.split(' ')[1].slice(0, -3);
    const tempUrl =
      showtime.data.ticketing.length > 0
        ? showtime.data.ticketing[0].urls[0]
        : '';

    if (dateCounter === -1 || showtimesData[dateCounter].date !== tempDate) {
      dateCounter += 1;
      showtimesData[dateCounter] = {
        date: tempDate,
        dateFormatted: tempDateFormatted,
        times: [
          {
            time: tempTime,
            url: tempUrl,
            tags: showtime.tags,
          },
        ],
      };
    } else {
      showtimesData[dateCounter].times.push({
        time: tempTime,
        url: tempUrl,
        tags: showtime.tags,
      });
    }
  });

  return showtimesData;
};
